import { ChevronRight, Maximize2 } from 'lucide-react'
import type { MouseEvent, ReactNode } from 'react'

import { AreaPolygonThumb } from '@/components/growers/area-map/area-polygon-thumb'
import { agriUnitLabel } from '@/lib/agri-unit-label'
import { m } from '@/lib/paraglide/messages'

/** Vùng chè của nhật ký — tên để hiện chip, toạ độ để vẽ ảnh bản đồ. */
export interface CareLogCardArea {
    id: string
    name: string
    acreage?: number
    paths?: unknown[]
    markers?: unknown[]
}

/** Một dòng vật tư đã dùng, kèm định lượng theo đơn vị của chính vật tư đó. */
export interface CareLogCardMaterial {
    name: string
    quantity: number
    unit?: string
    /** Hộ tự mua ngoài → không xuất kho HTX; hiện nhãn để phân biệt. */
    selfBought?: boolean
}

interface Props {
    /** Hộ trồng chè — dòng nhận diện chính của nhật ký. */
    grower: string
    /** Ngày giờ thực hiện, đã format sẵn. */
    logDate: string
    /** Nhãn hoạt động (Bón phân, Phun thuốc…). */
    activity: string
    quantity: number
    showQuantity?: boolean
    /** Vùng chè đã chăm sóc; rỗng thì ẩn cả chip lẫn ảnh bản đồ. */
    areas?: CareLogCardArea[]
    /** Vật tư + định lượng đã dùng; rỗng thì rơi về ô khối lượng cũ. */
    materials?: CareLogCardMaterial[]
    /** Nhãn của ô định lượng (Số lượng đã bón / đã phun / Sản lượng…). */
    amountLabel?: string
    /** Bấm ảnh bản đồ → phóng to vùng trồng. Không truyền thì ảnh chỉ để xem. */
    onOpenMap?: () => void
    /** Nút sửa/xoá; tự chặn nổi bọt để không mở màn sửa khi bấm. */
    actions?: ReactNode
    /** Chạm vào card = mở chi tiết nhật ký. */
    onClick?: () => void
}

/**
 * Card mobile của một nhật ký chăm sóc — cùng ngôn ngữ thị giác với
 * `LotListCard` (danh sách lô): tên ở trên, thông tin phụ mờ bên dưới, ô số
 * lượng nổi bật, ngày ở chân card. Bảng chỉ hợp desktop; trên điện thoại 5 cột
 * bị bóp lại hoặc phải cuộn ngang, hộ dân rất khó bấm đúng nút.
 *
 * Card trả lời đúng 3 câu hộ dân hỏi khi lướt danh sách: ai làm, làm ở vùng
 * nào, dùng hết bao nhiêu vật tư — nên vùng chè và định lượng nằm thẳng trên
 * card, không bắt mở chi tiết mới thấy.
 */
export function CareLogListCard({
    grower,
    logDate,
    activity,
    quantity,
    showQuantity = true,
    areas = [],
    materials = [],
    amountLabel,
    onOpenMap,
    actions,
    onClick,
}: Props) {
    const clickable = !!onClick
    const stopPropagation = (e: MouseEvent) => e.stopPropagation()
    const firstArea = areas[0]
    // Bón phân/Phun thuốc đã có định lượng theo từng vật tư → ô khối lượng cộng
    // dồn (cộng lẫn đơn vị) chỉ gây hiểu nhầm, chỉ giữ cho Hái chè.
    const showLegacyQuantity = showQuantity && materials.length === 0
    return (
        // Card KHÔNG mang role="button": ảnh bản đồ bên trong đã là nút riêng,
        // mà nút lồng trong nút thì trình đọc màn hình gộp hết nội dung card
        // thành một tên nút dài vô nghĩa. Chạm cả khung vẫn mở chi tiết (thao
        // tác quen tay trên điện thoại), còn bàn phím và trình đọc màn hình đi
        // qua nút mũi tên "Xem chi tiết" ở góc phải.
        <div
            onClick={onClick}
            className={`space-y-2.5 rounded-md border bg-card p-3 ${
                clickable ? 'cursor-pointer hover:bg-muted/50' : ''
            }`}
        >
            <div className="flex items-start gap-3">
                {firstArea && (
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation()
                            onOpenMap?.()
                        }}
                        disabled={!onOpenMap}
                        aria-label={m.care_log_view_map()}
                        className="relative size-16 shrink-0 overflow-hidden rounded-md border enabled:cursor-zoom-in"
                    >
                        <AreaPolygonThumb
                            paths={firstArea.paths}
                            markers={firstArea.markers}
                            className="size-full"
                        />
                        {areas.length > 1 && (
                            <span className="absolute left-1 top-1 rounded-full bg-black/60 px-1.5 text-[10px] font-semibold text-white">
                                {m.care_log_area_count({
                                    count: areas.length,
                                })}
                            </span>
                        )}
                        {onOpenMap && (
                            <span className="absolute bottom-1 right-1 rounded bg-black/60 p-0.5 text-white">
                                <Maximize2 className="size-2.5" />
                            </span>
                        )}
                    </button>
                )}
                <div className="min-w-0 flex-1 space-y-1">
                    <div className="break-words text-sm font-medium text-primary">
                        {grower}
                    </div>
                    <div>
                        <span className="inline-flex rounded-full bg-accent px-2 py-0.5 text-xs font-medium text-accent-foreground">
                            {activity}
                        </span>
                    </div>
                    <div className="text-xs tabular-nums text-muted-foreground">
                        {logDate}
                    </div>
                </div>
                {actions ? (
                    <div
                        className="flex shrink-0 items-center gap-1"
                        onClick={stopPropagation}
                    >
                        {actions}
                    </div>
                ) : (
                    clickable && (
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation()
                                onClick?.()
                            }}
                            aria-label={m.care_log_view_detail()}
                            className="-m-1 shrink-0 rounded-md p-1 text-muted-foreground"
                        >
                            <ChevronRight className="size-4" />
                        </button>
                    )
                )}
            </div>

            {areas.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                    {areas.map((area) => (
                        <span
                            key={area.id}
                            className="rounded-full bg-muted px-2 py-0.5 text-xs"
                        >
                            {area.name}
                            {area.acreage ? (
                                <span className="ml-1 tabular-nums text-muted-foreground">
                                    {m.care_log_area_acreage({
                                        acreage: area.acreage,
                                    })}
                                </span>
                            ) : null}
                        </span>
                    ))}
                </div>
            )}

            {materials.length > 0 && (
                <div className="space-y-1 rounded-md border bg-primary/5 px-3 py-2">
                    <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                        {amountLabel ?? m.care_log_amount_used()}
                    </div>
                    {materials.map((mat, i) => (
                        <div
                            key={`${mat.name}-${i}`}
                            className="flex items-baseline justify-between gap-3 text-sm"
                        >
                            <span className="min-w-0 break-words">
                                {mat.name}
                                {mat.selfBought && (
                                    <span className="ml-1 text-xs text-muted-foreground">
                                        {m.care_log_self_bought_tag()}
                                    </span>
                                )}
                            </span>
                            <span className="shrink-0 font-semibold tabular-nums text-primary">
                                {mat.quantity}
                                {mat.unit ? ` ${agriUnitLabel(mat.unit)}` : ''}
                            </span>
                        </div>
                    ))}
                </div>
            )}

            {showLegacyQuantity && (
                <div className="rounded-md bg-primary/10 px-3 py-2">
                    <div className="text-xs text-muted-foreground">
                        {amountLabel ?? m.care_log_quantity()}
                    </div>
                    <div className="text-lg font-semibold tabular-nums text-primary">
                        {quantity}
                    </div>
                </div>
            )}
        </div>
    )
}
